import { useEffect, useState } from "react";
import AutoCompleteUIDTO, { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import { ReportHeader } from "../external/ensolvers-core-frontend-mui/dto/ReportDTO";
import NotificationService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { trace } from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/Logging";
import WorkLogReport from "../models/WorkLogReport";
import ProjectService from "../services/ProjectService";
import WorkLogService from "../services/WorkLogService";
import { RequestStatus } from "../types/RequestStatus";
import OpsDateUtils from "../utils/OpsDateUtils";
import RoleUtils from "../utils/RoleUtils";
import { MAX_AUTOMATIC_HOURS_ADJUST, WorkLogsAdjusterResponse } from "../utils/WorklogsUtils";
import WorklogsUtils from "../utils/WorklogsUtils";
import useLocalStorage, { removeItem, STORAGE_KEYS } from "./useLocalStorage";
import { useStoredDate } from "./useStoredDate";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import { useLocation } from "react-router-dom";
import { Routes } from "../types/Routes";
import useUpdateUrl from "./useUpdateUrl";
import WorkLog from "../models/WorkLog";
import Position from "../models/Position";
import Assignation from "../models/Assignation";

interface WorkDaysPerCountry {
  country: string;
  count: number;
}
interface WorkdaysPerWeek {
  week: number;
  workdays: WorkDaysPerCountry[];
}

interface WorkdaysResponse {
  [key: string]: {
    [key: string]: number;
  };
}

const useWorklogReportList = () => {
  const [requestStatus, setRequestStatus] = useState<RequestStatus | undefined>(undefined);
  const [selectedDate, setSelectedDate] = useStoredDate();
  const [projectNames, setProjectNames] = useState<AutoCompleteUIDTO[]>([]);
  const [storedProject, setStoredProject] = useLocalStorage<AutoCompleteUIDTO>(STORAGE_KEYS.STORED_PROJECT, EMPTY_AUTOCOMPLETE);
  const [search, setSearch] = useState<AutoCompleteUIDTO>(storedProject);
  const [headers, setHeaders] = useState<ReportHeader[]>();
  const [rows, setRows] = useState<WorkLogReport[]>([]);
  const [canEdit, setCanEdit] = useState<boolean>(false);
  const [reportToAdjust, setReportToAdjust] = useState<WorkLogReport | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [onlyExternal, setOnlyExternal] = useLocalStorage<boolean>(STORAGE_KEYS.STORED_ONLY_EXTERNAL, false);
  const [assigned, setAssigned] = useLocalStorage<boolean>(STORAGE_KEYS.STORED_ASSIGNED_TO_ME, false);
  const [projectsAssigned, setProjectsAssigned] = useState<AutoCompleteUIDTO[]>([]);
  const [workdays, setWorkdays] = useState<WorkdaysPerWeek[]>();
  const [weeks, setWeeks] = useState<string[]>([]);
  const searchParams = new URLSearchParams(useLocation().search);
  const project = searchParams.get("project");
  const month = searchParams.get("month");
  const year = searchParams.get("year");
  const assignedToMe = searchParams.get("assigned");
  const onlyExternalParam = searchParams.get("onlyExternal");
  const [selectedReport, setSelectedReport] = useState<WorkLogReport | null>();
  const { updateUrl } = useUpdateUrl(Routes.WORKLOG_REPORT, searchParams);
  const [adjustFixed, setAdjustFixed] = useState<boolean>(false);
  const [batchReportsToAdjust, setBatchReportsToAdjust] = useState<WorkLogReport[] | undefined>(undefined);
  const [batchReportsToApprove, setBatchReportsToApprove] = useState<WorkLogReport[] | undefined>(undefined);
  const [positionUpdated, setPositionUpdated] = useState<Position | undefined>(undefined)
  const [assignationUpdated, setAssignationUpdated] = useState<Assignation | undefined>(undefined)

  useEffect(() => {
    loadProjectNames();
    setSearch(storedProject ? storedProject : EMPTY_AUTOCOMPLETE);
    if (RoleUtils.isAdmin()) {
      setCanEdit(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigned, onlyExternal]);

  useEffect(() => {
    if (search && selectedDate && projectNames.length > 0) {
      getWorkLogReports();
    }
  }, [search, selectedDate, reportToAdjust, batchReportsToAdjust, batchReportsToApprove, positionUpdated, assignationUpdated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (project !== null && projectsAssigned) {
      const projectFilter = projectsAssigned.find((p) => p.externalId === project);
      if (projectFilter) setSearch(projectFilter);
    }

    const date = new Date();
    const prevParams: string = searchParams.toString();
    if (month !== null) {
      date.setMonth(parseInt(month));
      setSelectedDate(date);
    } else {
      searchParams.set("month", (date.getMonth() - 1).toString());
    }

    if (year !== null) {
      date.setFullYear(parseInt(year));
      setSelectedDate(date);
    } else {
      searchParams.set("year", date.getFullYear().toString());
    }

    if (assignedToMe !== null) {
      setAssigned(assignedToMe === "true");
    } else if (assigned !== undefined) {
      searchParams.set("assigned", assigned.toString());
    }

    if (onlyExternalParam !== null) {
      setOnlyExternal(onlyExternalParam === "true");
    } else if (onlyExternal !== undefined) {
      searchParams.set("onlyExternal", onlyExternal.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsAssigned]);

  const handleOnChangeWorklogReport = () => {
    setAssigned(!assigned);
    setStoredProject(EMPTY_AUTOCOMPLETE);
    setSearch(EMPTY_AUTOCOMPLETE);
    removeItem("STORED_PROJECT");
    searchParams.set("assigned", (!assigned).toString());
    searchParams.delete("project");
    updateUrl();
  };

  const loadProjectNames = () => {
    setIsLoading(true);
    ProjectService.findAllProjectsNames(onlyExternal, assigned)
      .then((result) => {
        setProjectNames(result.getContent());
        getWorkLogReports();
      })
      .catch((error) => trace("Error getting project names", error))
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getWorkLogReports = async () => {
    setRequestStatus(RequestStatus.LOADING);

    (
      await WorkLogService.getWorkLogsPerWeek(
        search.externalId,
        selectedDate ? selectedDate.getFullYear() : new Date().getFullYear(),
        selectedDate ? selectedDate.getMonth() : new Date().getMonth()
      )
    )
      .onSuccess(async (response) => {        
        await checkIfUserIsManagerFromProject();
        setWorkdays(transformIntoWorkdays(response.getContent().additionalInformation));
        filterWeeks(response.getContent().headers);
        setHeaders(response.getContent().headers);
        const indexedRows = response.getContent().body.map((report, index) => {
          return { index, ...report };
        });
        setRows(indexedRows);
        setRequestStatus(RequestStatus.SUCCESS);
        setIsLoading(false);
      })
      .onError((response) => {
        setRequestStatus(RequestStatus.ERROR);
        NotificationService.error(response.getContent() || "Internal server error");
      });
  };

  const allWeeksApproved = (report: WorkLogReport): boolean => {
    if (headers) {
      //Check if the week is unchecked (adjustable) and if the days in that week are after the position start date.
      //The variable val used in the filter is an Object entry tuple [string, any].
      //In this case the string is the parsed week number and the other value is the boolean attached
      const unapprovedWeeks = Object.entries(report.validated as Map<number, boolean>).filter(
        (val) =>
          !val[1] &&
          OpsDateUtils.isAfter(
            OpsDateUtils.addWeeksToDate(new Date(`${selectedDate.getFullYear()}-01-01`), parseInt(val[0])),
            report.positionStartDate!
          )
      );

      return unapprovedWeeks.length === 0;
    }
    return false;
  };

  const handleAdjustHours = async () => {
    if (reportToAdjust) {
      if (allWeeksApproved(reportToAdjust)) {
        NotificationService.error("You can't adjust hours with all the weeks approved");
        return;
      }
      (await WorkLogService.adjustHours(reportToAdjust.assignationExternalId, reportToAdjust.coreUserExternalId, selectedDate.getFullYear(), selectedDate.getMonth(), reportToAdjust, adjustFixed))
      .onSuccess(async (response) => {
        NotificationService.info(response);
      })
      .onError((err) => {
        setRequestStatus(RequestStatus.ERROR);
        NotificationService.error(err || "Internal server error");
      });
      setReportToAdjust(undefined);
    }
  };

  const handleBatchAdjustHours = async () => {
    try {
      for (const reportToAdjust of batchReportsToAdjust || []) {
        if (!reportToAdjust) continue;
  
        if (allWeeksApproved(reportToAdjust)) {
          NotificationService.error("You can't adjust hours with all the weeks approved");
          continue;
        }
  
        (await WorkLogService.adjustHours(
          reportToAdjust.assignationExternalId,
          reportToAdjust.coreUserExternalId,
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          reportToAdjust,
          adjustFixed
        ))
        .onSuccess((response => {
          NotificationService.info(response);
        }))
        .onError(err => {
          setRequestStatus(RequestStatus.ERROR);
          NotificationService.error(err || "Internal server error");
        }) 
      }
      setBatchReportsToAdjust(undefined);
    } catch (err) {
    }
  };
  

  const checkIfUserIsManagerFromProject = async () => {
    if (search.externalId !== "" && !RoleUtils.isAdmin()) {
      const response = await ProjectService.isUserCoreManagerFromProject(search.externalId, AuthService.getUser()?.externalId!);
      setCanEdit(response.succeeded());
      localStorage.setItem(STORAGE_KEYS.STORED_ASSIGNED_TO_ME, response.succeeded().toString());
    }
  };

  const handleChangeExternalProjects = () => {
    setOnlyExternal(!onlyExternal);
    searchParams.set("onlyExternal", (!onlyExternal).toString());
    searchParams.delete("project");
    setStoredProject(EMPTY_AUTOCOMPLETE);
    updateUrl();
  };

  const clearWorklogReport = () => setReportToAdjust(undefined);
  const clearRequestStatus = () => setRequestStatus(undefined);
  const clearBatchReport = () => setBatchReportsToAdjust(undefined);

  const transformIntoWorkdays = (additionalInfo: WorkdaysResponse) => {
    let weeklyWorkdays: WorkdaysPerWeek[] = [];
    additionalInfo &&
      Object.entries(additionalInfo).forEach(([key, value]) => {
        let dailyWorkdays: WorkDaysPerCountry[] = [];
        Object.entries(value).map(([country, count]) => dailyWorkdays.push({ country, count }));
        weeklyWorkdays.push({ week: parseInt(key), workdays: dailyWorkdays });
      });
    return weeklyWorkdays;
  };

  const filterWeeks = (headers: ReportHeader[]) => {
    let tempWeeks: string[] = [];
    headers.forEach((h) => {
      if (h.type === "workdays") {
        tempWeeks.push(h.label);
      }
    });
    setWeeks(tempWeeks);
  };

  const findMinDayOfCurrentWeek = () => {
    const currentDate = new Date();
    const currentDay = currentDate.getDate();

    let intervalIndex = -1;

    for (let i = 0; i < weeks.length; i++) {
      const [start, end] = weeks[i].split(" - ");
      if (currentDay >= Number(start) && currentDay <= Number(end)) {
        intervalIndex = i;
        break;
      }
    }
    if (intervalIndex === -1) {
      return null;
    }

    const [start, end] = weeks[intervalIndex].split(" - ");
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    return new Date(currentYear, currentMonth, Number(start));
  };

  const onWorkLogUpdate = async (workLog: WorkLog, prevHours: number) => {
    if (!selectedReport) {
      return; // No selected report, nothing to update
    }
    const day: Date = new Date(workLog.shortDay);
    const weekNumber: number = OpsDateUtils.getWeekOfYear(day);
    const { hours, index } = selectedReport;
    hours[weekNumber] = hours[weekNumber] + (workLog.hours - prevHours);
  
    const updatedRows: WorkLogReport[] = rows.map(row => row.index === index ? {...row,hours: hours} : row);
    setRows(updatedRows);
  };

  return {
    worklogReportState: {
      reportToAdjust,
      setReportToAdjust,
      getWorkLogReports,
      clearWorklogReport,
      handleAdjustHours,
      allWeeksApproved,
      setAdjustFixed,
      adjustFixed,
      setBatchReportsToAdjust,
      setBatchReportsToApprove,
      batchReportsToApprove,
      batchReportsToAdjust,
      clearBatchReport,
      handleBatchAdjustHours,
      setPositionUpdated,
      setAssignationUpdated
    },
    requestStatus,
    clearRequestStatus,
    handleChangeExternalProjects,
    headers,
    rows,
    setRows,
    selectedDate,
    setSelectedDate,
    isLoading,
    setIsLoading,
    canEdit,
    search,
    setSearch,
    projectNames,
    setStoredProject,
    onlyExternal,
    handleOnChangeWorklogReport,
    setAssigned,
    assigned,
    projectsAssigned,
    workdays,
    storedProject,
    weeks,
    findMinDayOfCurrentWeek,
    selectedReport,
    setSelectedReport,
    onWorkLogUpdate,
  };
};

export default useWorklogReportList;
